/**
 * The platform at a glance, for its administrator: whether the accounts trade,
 * what they earned, what needs a hand now, and where each client stands.
 *
 * Each figure is read from the same endpoint the section that owns it reads,
 * and links there; nothing is computed that a section does not already state.
 * "Требует внимания" comes first after the figures because it is the one list
 * the administrator has to act on.
 */
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CircleCheck, TriangleAlert } from "lucide-react";
import { Link } from "react-router";

import { LeadStrip } from "@/design-system/LeadStrip";
import { PageHeader } from "@/design-system/PageHeader";
import { Panel } from "@/design-system/Panel";
import { poolColour } from "@/design-system/poolColour";
import { StatusBadge } from "@/design-system/StatusBadge";
import { readPlatformRegistry, type RegistryStrategyResponse } from "@/shared/api";
import { ClientProgress } from "@/shared/clients/ClientProgress";
import { useUnattributedDeposits } from "@/shared/clients/deposits";
import { useClients } from "@/shared/clients/directory";
import { useClientOperations } from "@/shared/clients/operations";
import { useOwnAccountKeys } from "@/shared/clients/ownAccounts";
import { incomeNote, periodProfit, platformIncome, PROOF_FRESH_SECONDS, roster, type ClientRow } from "@/shared/clients/roster";
import { money } from "@/shared/money/money";
import { releaseVerdict, shortSha } from "@/shared/release/releaseLanguage";
import { useReleaseStatus } from "@/shared/release/useReleaseStatus";
import { latestByStrategy } from "@/shared/strategy/registry";
import { plural } from "@/shared/text/plural";

const LINK =
  "inline-flex items-center gap-1 text-helper-sm text-text-secondary underline-offset-2 transition-colors duration-150 hover:text-text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring";

/** What a figure says while its source has not answered. Never a zero. */
const UNKNOWN = "—";

function unavailable(failed: boolean): string {
  return failed ? "источник недоступен" : "загружаю…";
}

function useRegistry(): { readonly versions: readonly RegistryStrategyResponse[] | null; readonly failed: boolean } {
  const query = useQuery({
    queryKey: ["strategy-registry", "platform", null, null],
    retry: false,
    queryFn: async () => {
      const result = await readPlatformRegistry();
      if (result.data === undefined) throw new Error("Strategy registry unavailable");
      return result.data;
    },
  });
  return { versions: query.data?.versions ?? null, failed: query.isError };
}

type Issue = { readonly key: string; readonly tone: "danger" | "warning"; readonly text: React.ReactNode; readonly to: string };

function Attention({ issues, known }: { readonly issues: readonly Issue[]; readonly known: boolean }): React.JSX.Element {
  return (
    <Panel title="Требует внимания" testId="platform-attention">
      {!known ? (
        <p className="text-body-main text-text-secondary">Проверяю…</p>
      ) : issues.length === 0 ? (
        <p className="flex items-center gap-2 text-body-main text-text-secondary">
          <CircleCheck aria-hidden="true" className="size-4 text-status-positive" />
          Всё в порядке — ничего делать не нужно.
        </p>
      ) : (
        <ul className="divide-y divide-border-subtle">
          {issues.map((issue) => (
            <li key={issue.key} className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0">
              <span className="flex items-start gap-2 text-body-main text-text-primary">
                <TriangleAlert aria-hidden="true" className={`mt-0.5 size-4 shrink-0 ${issue.tone === "danger" ? "text-status-negative" : "text-status-warning"}`} />
                <span>{issue.text}</span>
              </span>
              <Link to={issue.to} className={`${LINK} shrink-0`}>
                открыть <ArrowRight aria-hidden="true" className="size-3.5" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function issuesOf(rows: readonly ClientRow[], deposits: number, release: { readonly tone: string; readonly text: string } | null): Issue[] {
  const issues: Issue[] = rows.flatMap((row) =>
    row.attention.map((item) => ({
      key: `${row.key}-${item.text}`,
      tone: item.tone,
      to: "/app/clients",
      text: <><span className="font-medium">{row.client.display_name}</span> — {item.text}</>,
    })),
  );
  if (deposits > 0) {
    issues.push({
      key: "deposits",
      tone: "warning",
      to: "/app/clients",
      text: `${String(deposits)} ${plural(deposits, "оплата", "оплаты", "оплат")} комиссии с незнакомого UID Bybit`,
    });
  }
  if (release !== null && (release.tone === "danger" || release.tone === "warning")) {
    issues.push({ key: "release", tone: release.tone === "danger" ? "danger" : "warning", to: "/app/system", text: release.text });
  }
  return issues;
}

export function PlatformOverviewPage(): React.JSX.Element {
  const clients = useClients();
  const operations = useClientOperations();
  const ownKeys = useOwnAccountKeys();
  const deposits = useUnattributedDeposits();
  const { versions, failed: registryFailed } = useRegistry();
  const release = useReleaseStatus();

  const ready = release.status === "ready" ? release.release : null;
  const verdict = ready === null ? null : releaseVerdict(ready);
  const accounts = operations.data;
  const rows = clients.data === undefined ? null : roster(clients.data, accounts, ownKeys);
  const trading = accounts?.filter((item) => item.trading).length ?? 0;
  const lost = accounts?.filter(
    (item) => item.bybit_connected && (item.proof_age_seconds === null || item.proof_age_seconds > PROOF_FRESH_SECONDS),
  ) ?? [];
  // A source that failed is itself something to look at: "all in order" is
  // said only once every source has answered.
  const unread: Issue[] = [
    ...(clients.isError ? [{ key: "clients", tone: "danger" as const, to: "/app/clients", text: "Список клиентов недоступен" }] : []),
    ...(operations.isError ? [{ key: "operations", tone: "danger" as const, to: "/app/clients", text: "Сводка по счетам недоступна — связь и деньги сейчас неизвестны" }] : []),
    ...(deposits.isError ? [{ key: "deposits-unread", tone: "warning" as const, to: "/app/clients", text: "Оплаты комиссии не прочитаны" }] : []),
    ...(release.status !== "loading" && ready === null ? [{ key: "release-unread", tone: "warning" as const, to: "/app/system", text: "Состояние версии платформы недоступно" }] : []),
  ];
  const issues = [...unread, ...(rows === null ? [] : issuesOf(rows, deposits.data?.length ?? 0, verdict))];
  const settled = !clients.isPending && !operations.isPending && !deposits.isPending && release.status !== "loading";
  const profit = accounts === undefined ? null : periodProfit(accounts);
  const income = accounts === undefined ? null : platformIncome(accounts);
  // One row per strategy: its current version. Superseded ones stay in
  // «Стратегия»; listed here they read as paused duplicates (2026-10-13).
  const current = versions === null ? null : latestByStrategy(versions);
  const assignable = current?.filter((version) => version.available_for_assignment).length;
  const active = clients.data?.filter((client) => client.access === "ACTIVE").length;
  const invited = clients.data?.filter((client) => client.access === "INVITED").length;

  return (
    <div className="flex flex-col gap-2" data-testid="platform-overview">
      <PageHeader title="Обзор" description="Торгуют ли счета, сколько заработали и что требует твоего внимания." />
      <LeadStrip
        testId="platform-lead"
        cells={[
          {
            label: "Торговля",
            testId: "platform-trading",
            // From the client accounts, never from the release record: the web
            // release itself has no exchange authority, which read as "trading
            // off" while every account traded (2026-10-13).
            value: accounts === undefined ? UNKNOWN : `${String(trading)} из ${String(accounts.length)}`,
            note: accounts === undefined
              ? unavailable(operations.isError)
              : lost.length > 0
                ? <span className="text-status-negative">нет связи: {lost.map((item) => item.display_name).join(", ")}</span>
                : `${plural(accounts.length, "счёт торгует", "счетов торгуют", "счетов торгуют")} · связь в порядке`,
          },
          {
            label: "Прибыль клиентов",
            testId: "platform-profit",
            value: profit === null ? UNKNOWN : `${money(profit.total)} USDT`,
            note: profit === null
              ? unavailable(operations.isError)
              : profit.pending > 0 ? `за период · без ${String(profit.pending)} — ещё считается` : "за период, все счета",
          },
          {
            label: "Доход платформы",
            testId: "platform-fee",
            value: income === null ? UNKNOWN : `${money(income.paid)} USDT`,
            note: income === null ? unavailable(operations.isError) : incomeNote(income),
          },
          {
            label: "Клиенты",
            testId: "platform-clients-count",
            value: clients.data === undefined ? UNKNOWN : String(clients.data.length),
            note: clients.data === undefined ? unavailable(clients.isError) : `с доступом ${String(active)} · приглашено ${String(invited)}`,
          },
        ]}
      />
      <Attention issues={issues} known={settled} />
      <div className="grid grid-cols-1 gap-2 lg:grid-cols-12">
        <div className="min-w-0 lg:col-span-8">
          <Panel
            title="Клиенты"
            testId="platform-overview-clients"
            trailing={<Link to="/app/clients" className={LINK}>все клиенты <ArrowRight aria-hidden="true" className="size-3.5" /></Link>}
          >
            {rows === null ? (
              <p className="text-body-main text-text-secondary">{unavailable(clients.isError)}</p>
            ) : rows.length === 0 ? (
              <p className="text-body-main text-text-secondary">Клиентов пока нет. Добавь первого в разделе «Клиенты».</p>
            ) : (
              <ul className="divide-y divide-border-subtle">
                {rows.slice(0, 6).map((row) => (
                  <li key={row.key} className="grid grid-cols-1 items-center gap-2 py-2 first:pt-0 last:pb-0 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-body-main font-medium text-text-primary">{row.client.display_name}</span>
                      <span className="truncate text-helper-sm text-text-muted">{row.client.owner_login ?? UNKNOWN}</span>
                    </span>
                    <ClientProgress row={row} />
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </div>
        <div className="flex min-w-0 flex-col gap-2 lg:col-span-4">
          <Panel
            title="Стратегии"
            testId="platform-overview-strategies"
            trailing={<Link to="/app/strategies" className={LINK}>параметры <ArrowRight aria-hidden="true" className="size-3.5" /></Link>}
          >
            {versions === null ? (
              <p className="text-body-main text-text-secondary">{unavailable(registryFailed)}</p>
            ) : (
              <>
                <p className="mb-2 text-helper-sm text-text-muted" data-testid="platform-strategies-count">
                  {current?.length ?? 0} {plural(current?.length ?? 0, "пул", "пула", "пулов")} · можно назначать {String(assignable)}
                </p>
                <ul className="divide-y divide-border-subtle">
                  {(current ?? []).map((version) => (
                    <li key={version.version_id} className="flex items-center justify-between gap-3 py-2 last:pb-0">
                      <span className="flex items-center gap-2 text-body-main font-medium text-text-primary">
                        <span aria-hidden="true" className={`size-2 shrink-0 rounded-full ${poolColour(version.strategy_id)}`} />
                        {version.strategy_id}
                      </span>
                      <StatusBadge tone={version.available_for_assignment ? "ready" : "muted"}>
                        {version.available_for_assignment ? "можно назначать" : "приостановлена"}
                      </StatusBadge>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Panel>
          <Panel
            title="Версия платформы"
            testId="platform-release"
            trailing={<Link to="/app/system" className={LINK}>подробнее <ArrowRight aria-hidden="true" className="size-3.5" /></Link>}
          >
            {ready === null || verdict === null ? (
              <p className="text-body-main text-text-secondary">{unavailable(release.status !== "loading")}</p>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <StatusBadge tone={verdict.tone}>{verdict.text}</StatusBadge>
                <span className="font-mono text-data-mono text-text-secondary">{shortSha(ready.production_sha)}</span>
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
